/**
 * The document tab: the policy page exactly as it was before tabs existed.
 *
 * Moved here unchanged rather than rebuilt — the run's headline facts, the
 * document itself, what regulatory monitoring matched to it, then the run
 * (settings, live console, tally, chunks) are the most load-bearing thing on
 * this page, and reworking them for the sake of a new shell would have been a
 * regression dressed up as a redesign.
 *
 * @module components/regulation/policy/PolicyDocumentTab
 */
import { useNavigate } from "@tanstack/react-router";
import { FileTextIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { DocumentDetailsGrid } from "../document/DocumentDetailsGrid";
import { DocumentHeader } from "../document/DocumentHeader";
import { DocumentInfoCard } from "../document/DocumentInfoCard";
import { DocumentStatusAlert } from "../document/DocumentStatusAlert";
import { PolicyImpactCard } from "../document/PolicyImpactCard";
import { ExtractedTerms } from "../document/ExtractedTerms";
import { ProcessingPanel } from "../document/ProcessingPanel";
import { UNAVAILABLE_MESSAGES, useOrgProfile } from "../../../state/orgProfile";
import {
  usePolicies,
  usePolicyChunks,
  usePolicyJobs,
  usePolicyProcessing,
  useRegulatoryUpdates,
} from "../../../state/policies";

/** A running job's duration ticks; a finished page has no reason to re-render. */
function useClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

/**
 * Which file version this view is about. Processing runs are recorded per
 * policy, so the version's window (from its upload to the next one) is what
 * selects its runs; `null` as `to` means "still in force".
 */
export interface DocumentVersionScope {
  readonly version: number;
  readonly current: boolean;
  readonly from: string;
  readonly to: string | null;
}

export function PolicyDocumentTab({
  organizationId,
  policyId,
  orgSegment,
  scope,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
  readonly orgSegment: string;
  /** Absent: the current file, latest run. Present: that version's runs only. */
  readonly scope?: DocumentVersionScope;
}) {
  const navigate = useNavigate();
  const { snapshot, unavailableReason, isPending, download, remove } =
    usePolicies(organizationId);
  const { canManage } = useOrgProfile(organizationId);
  const { snapshot: updates } = useRegulatoryUpdates(organizationId);

  // `null` means the latest run — what the page opens on and polls.
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  // A superseded version has no "latest run"; its runs must be listed to
  // pick from, so they are requested up front.
  const superseded = scope !== undefined && !scope.current;
  const [jobsRequested, setJobsRequested] = useState(superseded);
  const [deleting, setDeleting] = useState(false);

  const { jobs: allJobs, isPending: jobsPending } = usePolicyJobs(
    organizationId,
    policyId,
    jobsRequested,
  );
  // Only the runs that read this version's file.
  const jobs = useMemo(() => {
    if (scope === undefined) return allJobs;
    const from = Date.parse(scope.from);
    const to = scope.to === null ? Number.POSITIVE_INFINITY : Date.parse(scope.to);
    return allJobs.filter((job) => {
      const at = Date.parse(job.createdAt);
      return at >= from && at < to;
    });
  }, [allJobs, scope]);
  useEffect(() => {
    if (!superseded || selectedJobId !== null) return;
    const latestInWindow = jobs[0];
    if (latestInWindow) setSelectedJobId(latestInWindow.id);
  }, [superseded, jobs, selectedJobId]);
  // A superseded version with no run of its own shows nothing rather than
  // the current file's live run wearing its number.
  const { state: liveProcessing } = usePolicyProcessing(
    organizationId,
    superseded && selectedJobId === null ? null : policyId,
    selectedJobId,
  );
  const processing = superseded && selectedJobId === null ? null : liveProcessing;
  const now = useClock(processing?.active ?? false);
  // The vocabulary panel reads the same chunks the run produces, so it fills in
  // as extraction proceeds and needs no request of its own.
  const { chunks: extractedChunks } = usePolicyChunks(organizationId, policyId, true);

  const policy = snapshot?.policies.find((candidate) => candidate.id === policyId) ?? null;
  const jurisdictionNames = useMemo(
    () => new Map((snapshot?.jurisdictions ?? []).map((item) => [item.slug, item.name])),
    [snapshot],
  );
  const impacting = useMemo(
    () => (updates?.updates ?? []).filter((update) => update.affectedPolicyIds.includes(policyId)),
    [policyId, updates],
  );

  if (policy === null) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border/60 bg-card px-6 py-16 text-center">
        <span className="mb-3 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <FileTextIcon className="size-5" />
        </span>
        <p className="text-base font-medium text-foreground">
          {isPending ? "Loading document…" : "Document not found"}
        </p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          {unavailableReason !== null
            ? UNAVAILABLE_MESSAGES[unavailableReason]
            : "This policy is not in your organization's library."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <DocumentHeader
        policy={policy}
        jobs={jobs}
        jobsPending={jobsPending}
        selectedJobId={selectedJobId}
        canManage={canManage}
        deleting={deleting}
        now={now}
        onOpenJobs={() => setJobsRequested(true)}
        onSelectJob={setSelectedJobId}
        onDownload={() => download(policy.id, scope?.current === false ? scope.version : undefined)}
        onDelete={() => {
          setDeleting(true);
          void remove(policy.id).then((message) => {
            setDeleting(false);
            // Staying on a page whose document no longer exists would only
            // render "not found"; go back to the library instead.
            if (message === null) {
              void navigate({
                to: "/$org/compliance/policies",
                params: { org: orgSegment },
              });
            }
          });
        }}
      />

      <DocumentStatusAlert
        error={processing?.status === "failed" ? (processing.error ?? null) : null}
      />

      <DocumentDetailsGrid
        createdAt={policy.createdAt}
        sourceType={policy.sourceType}
        processing={processing}
        jobs={jobs}
        now={now}
      />

      <DocumentInfoCard
        policy={policy}
        processing={processing}
        jurisdictionNames={jurisdictionNames}
        onDownload={() => download(policy.id, scope?.current === false ? scope.version : undefined)}
      />

      <PolicyImpactCard
        updates={impacting}
        monitoringEnabled={updates?.monitoringEnabled ?? false}
      />

      {!superseded && (
        <ExtractedTerms chunks={extractedChunks} active={processing?.active ?? false} />
      )}

      <ProcessingPanel
        organizationId={organizationId}
        policyId={policy.id}
        processing={processing}
        now={now}
      />
    </div>
  );
}
